import type {
  ScaleDetectedObjects,
  ScaleCircuitPaths,
  ScaleCablePaths,
  ScaleReshapeView,
  ScaleRouteCreationOverlay,
  ScalePolygonsOverlay,
  ScaleFloorPlanLayoutOverlay,
} from "./types";

export const scaleDetectedObjects: ScaleDetectedObjects = ({
  objects,
  transform,
}) =>
  objects.map((object) => ({
    ...object,
    bbox: transform.toDisplayBbox(object.bbox),
  }));

export const scaleCircuitPaths: ScaleCircuitPaths = ({ state, transform }) => ({
  ...state,
  paths: state.paths.map((path) => ({
    ...path,
    waypoints: path.waypoints.map(transform.toDisplay),
    coordinateExtents: transform.toDisplayRect(path.coordinateExtents),
  })),
  distributionBoardPosition: state.distributionBoardPosition
    ? transform.toDisplay(state.distributionBoardPosition)
    : null,
  // Coordinates like any other, and the reason they are here at all: a drawn
  // length that is not scaled with the cable lands in a heap in the corner of
  // the sheet, which is the bug this file exists for.
  ...(state.strandedEdges
    ? {
        strandedEdges: state.strandedEdges.map((edge) => ({
          a: transform.toDisplay(edge.a),
          b: transform.toDisplay(edge.b),
        })),
      }
    : {}),
});

export const scaleCablePaths: ScaleCablePaths = ({
  cablePaths,
  transform,
}) => ({
  ...cablePaths,
  nodes: cablePaths.nodes.map((node) => {
    const point = transform.toDisplay({
      x: parseFloat(node.x),
      y: parseFloat(node.y),
    });
    return { ...node, x: String(point.x), y: String(point.y) };
  }),
});

export const scaleRouteCreationOverlay: ScaleRouteCreationOverlay = ({
  overlay,
  transform,
}) => ({
  ...overlay,
  routes: overlay.routes.map((route) => ({
    ...route,
    nodes: route.nodes.map((node) => ({
      ...node,
      ...transform.toDisplay(node),
    })),
  })),
  hoverPosition: overlay.hoverPosition
    ? transform.toDisplay(overlay.hoverPosition)
    : null,
});

// Every field written out by hand and nothing spread: a new member on
// `CableReshapeView` has to be a compile error here, or the next one added
// silently keeps the marks in the space the cable left behind.
export const scaleReshapeView: ScaleReshapeView = ({ view, transform }) => ({
  edges: view.edges.map((edge) => ({
    key: edge.key,
    a: transform.toDisplay(edge.a),
    b: transform.toDisplay(edge.b),
    ...transform.toDisplay({ x: edge.x, y: edge.y }),
    hasAdd: edge.hasAdd,
    isUsed: edge.isUsed,
    isSelected: edge.isSelected,
  })),
  nodes: view.nodes.map((node) => ({
    key: node.key,
    ...transform.toDisplay({ x: node.x, y: node.y }),
    isDraggable: node.isDraggable,
  })),
  unreachable: view.unreachable.map(transform.toDisplay),
  // Not coordinates: which thing is showing its signs, and what they armed.
  sign: view.sign,
  drawFrom: view.drawFrom,
  // Coordinates: the bends of a stroke in progress land on the same sheet as
  // the cable, or they land in the corner of it.
  drawPoints: view.drawPoints.map(transform.toDisplay),
  carrying: view.carrying,
  cursor: view.cursor ? transform.toDisplay(view.cursor) : null,
  // Not a coordinate: the marks report their own clicks, so what a mark REACHES
  // is decided on the plan, in screen pixels, and never travels through here.
  onMark: view.onMark,
});

export const scalePolygonsOverlay: ScalePolygonsOverlay = ({
  overlay,
  transform,
}) => ({
  ...overlay,
  polygons: overlay.polygons.map((polygon) => ({
    ...polygon,
    points: polygon.points.map(transform.toDisplay),
  })),
  hoverPosition: overlay.hoverPosition
    ? transform.toDisplay(overlay.hoverPosition)
    : null,
});

export const scaleFloorPlanLayoutOverlay: ScaleFloorPlanLayoutOverlay = ({
  overlay,
  transform,
}) => {
  // Lengths survive the quarter turns, so one unit along x measures the scale for the zone stroke.
  const origin = transform.toDisplay({ x: 0, y: 0 });
  const unit = transform.toDisplay({ x: 1, y: 0 });
  const scale = Math.hypot(unit.x - origin.x, unit.y - origin.y);
  return {
    view: overlay.view,
    zoneStrokeWidth: overlay.zoneStrokeWidth * scale,
    highlightedShapeId: overlay.highlightedShapeId,
    shapes: overlay.shapes.map((shape) => ({
      ...shape,
      rings: shape.rings.map((ring) => ring.map(transform.toDisplay)),
      label: shape.label && {
        text: shape.label.text,
        points: shape.label.points.map(transform.toDisplay),
      },
    })),
  };
};
